import {
  useCallback, useEffect, useRef,
} from 'react';
import { useDispatch } from 'react-redux';
import type { SpreadsheetRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { getExternallyResolvedPromise } from '~/_shared/utils/promise/externallyResolvedPromise';
import { useLatLngSpreadsheetData } from '~/map/map/useSpreadsheetData.hook';
import { getGroupProximityLocations } from '~/proximityDetails/proximityDetails.helpers';
import { activeMapElementsSetActiveProximity } from '~/store/frontendState/activeMapElements/activeMapElements.actionCreators';
import type { SpreadsheetLatLngRowData } from '~/store/selectors/spreadsheetDataMemoizedSelectors';
import { useSpreadsheetDataDataSelector } from '~/store/spreadsheetData/spreadsheetData.selectors';
import type { SpreadsheetDataData } from '~/store/spreadsheetData/spreadsheetData.state';
import { ProximityType } from '../_shared/types/proximity/proximity.enums';
import {
  isDriveTimePolygon, isGroupRadius, isIndividualRadius, type Proximity,
} from '../_shared/types/proximity/proximity.types';
import {
  getBoundingBoxOfCircle, getBoundingBoxOfMultipolygon,
} from '../_shared/utils/gis/boundingBox.helpers';
import { calculateRadiusInMeters } from '../spreadsheet/filter/radius/spreadsheetFilterRadius.helpers';
import { mapComponentSetZoomToBounds } from '../store/frontendState/mapComponent/mapComponent.actionCreators';
import { useProximities } from './useProximities';

type AwaitedSpreadsheetData = {
  readonly spreadsheetData: SpreadsheetDataData;
  readonly latLngLookup: SpreadsheetLatLngRowData;
};

export const useZoomToProximity = () => {
  const dispatch = useDispatch();
  const proximityList = useProximities();
  const spreadsheetDataOrNot = useSpreadsheetDataDataSelector();
  const latLngLookup = useLatLngSpreadsheetData();
  const spreadsheetDataPromiseRef = useRef(getExternallyResolvedPromise<AwaitedSpreadsheetData>());

  useEffect(() => {
    if (spreadsheetDataPromiseRef.current.isResolved()) {
      spreadsheetDataPromiseRef.current = getExternallyResolvedPromise<AwaitedSpreadsheetData>();
    }
    if (spreadsheetDataOrNot) {
      spreadsheetDataPromiseRef.current.resolve({ spreadsheetData: spreadsheetDataOrNot, latLngLookup });
    }
  }, [latLngLookup, spreadsheetDataOrNot]);

  const zoomToRadius = useCallback(async (proximity: Proximity, specificGroupProximityRowId?: SpreadsheetRowId) => {
    if (isGroupRadius(proximity)) {
      const radiusData = proximity.data;
      const { spreadsheetData, latLngLookup } = await spreadsheetDataPromiseRef.current;
      const groupProximityLocations = getGroupProximityLocations(spreadsheetData, proximity);

      if (groupProximityLocations[0]) {
        const specificRowIdLatLng = specificGroupProximityRowId ? latLngLookup.getRow(specificGroupProximityRowId) : null;
        if (specificGroupProximityRowId && !specificRowIdLatLng) {
          return;
        }

        const firstProximityLatLng = latLngLookup.getRow(groupProximityLocations[0]);
        const targetProximityLatLng = specificRowIdLatLng ?? firstProximityLatLng;

        if (targetProximityLatLng) {
          const radiusInMeters = calculateRadiusInMeters(radiusData.radius, radiusData.unit);
          const boundingBox = getBoundingBoxOfCircle(targetProximityLatLng, radiusInMeters);
          dispatch(activeMapElementsSetActiveProximity(proximity.id, targetProximityLatLng));
          dispatch(mapComponentSetZoomToBounds(boundingBox));
        }
      }
    }
    else if (isIndividualRadius(proximity)) {
      const radiusData = proximity.data;
      const proximityLatLng = { lat: radiusData.data.lat, lng: radiusData.data.lng };
      const radiusInMeters = calculateRadiusInMeters(radiusData.radius, radiusData.unit);

      const boundingBox = getBoundingBoxOfCircle(proximityLatLng, radiusInMeters);
      dispatch(mapComponentSetZoomToBounds(boundingBox));
    }
  }, [dispatch]);

  const zoomToProximity = useCallback((proximity: Proximity, specificGroupProximityRowId?: SpreadsheetRowId) => {
    if (proximity.type === ProximityType.DistanceRadius) {
      zoomToRadius(proximity, specificGroupProximityRowId);
    }
    else if (isDriveTimePolygon(proximity) && proximity.data.paths) {
      dispatch(mapComponentSetZoomToBounds(getBoundingBoxOfMultipolygon(proximity.data.paths)));
    }
  }, [dispatch, zoomToRadius]);

  const zoomById = useCallback((proximityId: string) => {
    const proximity = proximityList.find(p => p.id === proximityId);
    if (proximity) {
      zoomToProximity(proximity);
    }
  }, [proximityList, zoomToProximity]);

  return {
    zoomToRadius,
    zoomById,
    zoomToProximity,
  };
};
